<template>
    <div class="video-manager">
        <h1>Video Manager</h1>

        <form
            class="video-form"
            @submit.prevent="isEditing ? updateVideo() : createVideo()"
        >
            <div class="form-group">
                <label for="title">Title</label>
                <input
                    v-model="videoForm.title"
                    type="text"
                    required
                    placeholder="Enter video title"
                />
            </div>
            <div class="form-group">
                <label for="videoUrl">Google Drive Video URL</label>
                <input
                    v-model="videoForm.videoUrl"
                    type="text"
                    required
                    placeholder="Enter Google Drive video URL"
                />
            </div>
            <v-btn type="submit" color="#967adc" dark small>
                {{ isEditing ? 'Update Video' : 'Create Video' }}
            </v-btn>
        </form>

        <h2>Existing Videos</h2>
        <div v-if="videos.length === 0" class="no-videos">
            No videos available
        </div>
        <ul class="video-list">
            <li v-for="video in videos" :key="video.id" class="video-item">
                <div class="video-item__content">
                    <div class="video-item__preview">
                        <iframe
                            :src="video.videoUrl"
                            class="video-frame"
                            allow="autoplay; encrypted-media"
                            width="100%"
                            height="100%"
                        ></iframe>
                    </div>
                    <div class="video-item__qr-code">
                        <h3 class="video-item__title">
                            {{ video.title }}
                        </h3>
                        <a :href="video.qrCode" download="qr-code.png">
                            <img
                                :src="video.qrCode"
                                alt="QR Code"
                                class="qr-code"
                            />
                        </a>
                    </div>
                    <div class="video-item__actions">
                        <v-btn
                            dark
                            dense
                            small
                            rounded
                            color="#da4453"
                            outlined
                            icon
                            @click="deleteVideo(video.id)"
                        >
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>

                        <v-btn
                            class="video-item__edit-btn"
                            dark
                            dense
                            small
                            rounded
                            color="#f6bb42"
                            @click="editVideo(video)"
                        >
                            <v-icon x-small>mdi-pencil</v-icon>
                            Edit
                        </v-btn>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
import axios from 'axios'
import { mapActions } from 'vuex'
import { actionTypes as actionTypesConfirmer } from '@/store/modules/confirmer'

export default {
    data() {
        return {
            videos: [],
            videoForm: {
                id: null,
                title: '',
                videoUrl: '',
            },
            isEditing: false,
            apiBaseUrl: '/wig-care-hub-api/api', // Change to your API base URL
        }
    },
    created() {
        this.fetchVideos()
    },
    methods: {
        ...mapActions('confirmer', {
            confirmHandle: actionTypesConfirmer.confirmHandle,
        }),
        async fetchVideos() {
            try {
                const response = await axios.get(`${this.apiBaseUrl}/videos`)
                this.videos = response.data
            } catch (error) {
                console.error('Error fetching videos:', error)
            }
        },
        async createVideo() {
            try {
                const response = await axios.post(`${this.apiBaseUrl}/videos`, {
                    title: this.videoForm.title,
                    videoUrl: this.videoForm.videoUrl,
                })
                this.videos.push(response.data)
                this.fetchVideos()
                this.resetForm()
            } catch (error) {
                console.error('Error creating video:', error)
            }
        },
        editVideo(video) {
            this.videoForm = { ...video }
            this.isEditing = true
        },
        async updateVideo() {
            try {
                await axios.put(
                    `${this.apiBaseUrl}/videos/${this.videoForm.id}`,
                    {
                        videoUrl: this.videoForm.videoUrl,
                        title: this.videoForm.title,
                    }
                )
                const index = this.videos.findIndex(
                    (v) => v.id === this.videoForm.id
                )
                this.videos.splice(index, 1, { ...this.videoForm })
                this.resetForm()
                this.fetchVideos()
            } catch (error) {
                console.error('Error updating video:', error)
            }
        },
        async deleteVideo(id) {
            try {
                await this.confirmHandle(
                    'Are you sure you want to delete this video?'
                )
                await axios.delete(`${this.apiBaseUrl}/videos/${id}`)
                this.videos = this.videos.filter((video) => video.id !== id)
            } catch (error) {
                console.error('Error deleting video:', error)
            }
        },
        downloadQRCode(qrCodeData, videoTitle) {
            const link = document.createElement('a')
            link.href = qrCodeData
            link.download = `${videoTitle}-qr-code.png`
            link.click()
        },
        resetForm() {
            this.videoForm = {
                id: null,
                title: '',
                videoUrl: '',
            }
            this.isEditing = false
        },
    },
}
</script>

<style lang="scss" scoped>
.video-frame {
    border: 0;
}

.video-manager {
    padding: 20px;
    max-width: 900px;
    margin: 0 auto;

    h1 {
        text-align: center;
        margin-bottom: 20px;
        font-size: 2.5rem;
    }

    .video-form {
        margin-bottom: 40px;
        .form-group {
            margin-bottom: 15px;
            label {
                font-size: 1.1rem;
            }
            input {
                padding: 8px;
                width: 100%;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 1rem;
                &:focus {
                    border-color: #007bff;
                }
            }
        }
        .btn {
            padding: 8px 12px;
            font-size: 1rem;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            &.btn-primary {
                background-color: #007bff;
                color: #fff;
            }
        }
    }

    .video-list {
        list-style: none;
        padding: 0;
    }
}

.video-item {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.15);
    padding: 16px;

    & + & {
        margin-top: 16px;
    }

    &__content {
        display: flex;
        justify-content: flex-start;
        align-content: stretch;
        height: 100%;
    }

    &__title {
        font-size: 1.2rem;
        margin-bottom: 10px;
        white-space: nowrap;
    }

    &__preview {
        width: 320px;
        aspect-ratio: 16/9;
        margin-right: 16px;
        border-radius: 8px;
        overflow: hidden;
        background: #000;
    }

    &__qr-code {
        margin-right: 16px;

        img {
            border: 1px solid #b6b6b6;
            border-radius: 8px;
            width: 100px;
        }
    }

    &__actions {
        margin-left: auto;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-end;
        width: 150px;
        gap: 8px;
        border-left: 1px dotted #d9d9d9;
        padding-left: 20px;
    }

    &__edit-btn {
        width: 100%;
    }
}
</style>
